import clsx, { type ClassValue } from 'clsx';
import { ExerciseOption, CustomExercise, Challenge, Participant } from '@/types';
import { EXERCISE_OPTIONS, MOTIVATIONAL_QUOTES } from '@/constants';

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function generateId(): string {
  return crypto.randomUUID();
}

export function generateChallengeId(): string {
  // Generate a 6-character friendly ID using letters and numbers (avoiding confusing characters)
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Excludes I, O, 0, 1 for clarity
  let result = '';
  const array = new Uint8Array(6);
  crypto.getRandomValues(array);

  for (let i = 0; i < 6; i++) {
    result += chars[array[i] % chars.length];
  }

  return result;
}

export function getCurrentDate(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function getDaysInMonth(startDateStr: string, endDateStr?: string): number {
  if (endDateStr) {
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    const diffTime = end.getTime() - start.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }

  const d = new Date(startDateStr);
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

export function getDaysRemaining(startDate: string, endDate: string): number {
  const today = new Date();
  const end = new Date(endDate);
  const diffTime = end.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function calculateDailyTarget(challenge: Challenge, participant?: Participant): number {
  if (!challenge) return 0;

  if (!participant) {
    const totalChallengeDays = getDaysInMonth(challenge.startDate, challenge.endDate);
    const restDaysAllowed = challenge.restDaysAllowed || 0;
    const activeDays = Math.max(totalChallengeDays - restDaysAllowed, 1);
    return Math.ceil(challenge.target / activeDays);
  }

  const today = getCurrentDate();
  const restDays = participant.restDays || [];

  // If today is a rest day, return 0
  if (restDays.includes(today)) {
    return 0;
  }

  // Calculate remaining active days (excluding rest days)
  let activeDaysRemaining = 0;
  const currentDate = new Date(today + 'T00:00:00.000Z');
  const endDate = new Date(challenge.endDate + 'T00:00:00.000Z');

  const tempDate = new Date(currentDate);
  while (tempDate <= endDate) {
    const dateStr = tempDate.toISOString().split('T')[0];
    if (!restDays.includes(dateStr)) {
      activeDaysRemaining++;
    }
    tempDate.setDate(tempDate.getDate() + 1);
  }

  const todaysProgress = participant.dailyLog[today] || 0;
  const totalCompletedExcludingToday = participant.totalAmount - todaysProgress;
  const remainingAmount = challenge.target - totalCompletedExcludingToday;

  if (remainingAmount <= 0 || activeDaysRemaining <= 0) {
    return 0;
  }

  return Math.ceil(remainingAmount / activeDaysRemaining);
}

export function calculateStreak(participant: Participant, challenge: Challenge): number {
  if (!participant || !challenge) return 0;

  // Use getCurrentDate for consistency
  const todayStr = getCurrentDate();
  const today = new Date(todayStr + 'T00:00:00.000Z');

  const startDate = new Date(challenge.startDate + 'T00:00:00.000Z');
  const endDate = new Date(challenge.endDate + 'T00:00:00.000Z');

  let streak = 0;
  let currentDate = new Date(today);

  // Don't count future dates or dates before challenge started
  if (currentDate > endDate) currentDate = new Date(endDate);
  if (currentDate < startDate) return 0;

  // Get rest days for this participant
  const restDays = participant.restDays || [];
  const isRestDay = (dateStr: string) => restDays.includes(dateStr);

  // Function to calculate what the daily target was for a specific date
  const calculateDailyTargetForDate = (date: Date): number => {
    const dateStr = date.toISOString().split('T')[0];

    // If it's a rest day, target is 0
    if (isRestDay(dateStr)) return 0;

    // Calculate remaining reps and days as of the START of this date
    // We need to calculate what the target was when this day began
    let totalRepsBeforeThisDate = 0;
    for (const [logDate, reps] of Object.entries(participant.dailyLog)) {
      if (logDate < dateStr) {
        totalRepsBeforeThisDate += reps;
      }
    }

    const remainingRepsAtStartOfDay = challenge.target - totalRepsBeforeThisDate;

    // Count remaining active days (excluding rest days)
    let activeDaysRemaining = 0;
    const tempDate = new Date(date);
    while (tempDate <= endDate) {
      const tempDateStr = tempDate.toISOString().split('T')[0];
      if (!isRestDay(tempDateStr)) {
        activeDaysRemaining++;
      }
      tempDate.setDate(tempDate.getDate() + 1);
    }

    if (activeDaysRemaining <= 0) return 0;
    const target = Math.ceil(remainingRepsAtStartOfDay / activeDaysRemaining);

    return target;
  };

  // Start from today and work backwards to find the current streak
  // Count only active days (non-rest days) that met targets
  let checkDate = new Date(today);
  const challengeStillActive = today <= endDate;

  // First, check if today should contribute to the streak
  let todayContributesToStreak = false;
  const todayReps = participant.dailyLog[todayStr] || 0;

  if (challengeStillActive && !isRestDay(todayStr) && todayReps > 0) {
    const todayTarget = calculateDailyTargetForDate(today);
    if (todayReps >= todayTarget) {
      todayContributesToStreak = true;
      streak = 1; // Start with 1 for today
    }
  }

  // Now check backwards from yesterday
  checkDate.setDate(checkDate.getDate() - 1);
  let streakContinues = todayContributesToStreak || (challengeStillActive && isRestDay(todayStr));

  while (checkDate >= startDate && streakContinues) {
    const dateStr = checkDate.toISOString().split('T')[0];
    const dailyReps = participant.dailyLog[dateStr] || 0;
    const dailyTargetForDate = calculateDailyTargetForDate(checkDate);

    if (isRestDay(dateStr)) {
      // Rest days maintain the streak but don't add to the count
      // Just continue checking previous days
    } else if (dailyReps >= dailyTargetForDate) {
      // This day met the target, increment streak
      streak++;
    } else {
      // This day didn't meet target and wasn't a rest day - streak is broken
      streakContinues = false;
      break;
    }

    // Move to previous day
    checkDate.setDate(checkDate.getDate() - 1);
  }

  // If today is a rest day and we haven't started counting,
  // we need to check if there's an existing streak to maintain
  if (challengeStillActive && isRestDay(todayStr) && !todayContributesToStreak) {
    // Today is a rest day, so we maintain whatever streak was built up to yesterday
    // The streak value calculated above from working backwards is correct
  }

  return streak;
}

export function isChallengeCompleted(challenge: Challenge, participant: Participant): boolean {
  // IMPORTANT: Only trust the totalAmount if it's been calculated from logs
  // Optimistic updates can make this inaccurate
  // The participant data should always be recalculated from exercise logs for accuracy
  return participant.totalAmount >= challenge.target;
}

export function isChallengeFailed(challenge: Challenge, participant: Participant): boolean {
  // Calculate days remaining from today to challenge end date
  const today = new Date();
  const endDate = new Date(challenge.endDate);
  const diffTime = endDate.getTime() - today.getTime();
  const daysRemaining = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  // Challenge is failed if it's past the end date AND target not reached
  return daysRemaining < 0 && participant.totalAmount < challenge.target;
}

export function getExerciseForChallenge(challenge: Challenge): ExerciseOption | CustomExercise {
  if (challenge.customExercise) {
    return challenge.customExercise;
  }

  return EXERCISE_OPTIONS.find(ex => ex.id === challenge.exerciseType) || EXERCISE_OPTIONS[0];
}

export function getMotivationalQuote(_progress: number, totalProgress: number): string {
  const quotes =
    totalProgress >= 1
      ? MOTIVATIONAL_QUOTES.complete
      : totalProgress >= 0.8
        ? MOTIVATIONAL_QUOTES.high
        : totalProgress >= 0.5
          ? MOTIVATIONAL_QUOTES.medium
          : totalProgress >= 0.1
            ? MOTIVATIONAL_QUOTES.low
            : MOTIVATIONAL_QUOTES.start;

  return quotes[Math.floor(Math.random() * quotes.length)];
}

export function getIncrementsForUnit(unit: string): number[] {
  switch (unit) {
    case 'minutes':
      return [-5, -1, -0.5, 0.5, 1, 5];
    case 'meters':
      return [-100, -50, -10, 10, 50, 100];
    case 'kilograms':
      return [-10, -5, -1, 1, 5, 10];
    default:
      return [-10, -5, -1, 1, 5, 10];
  }
}

export function formatExerciseAmount(amount: number, unit: string): string {
  if (unit === 'minutes') {
    return amount.toFixed(1);
  }
  return amount.toString();
}

export function validateExerciseAmount(amount: number, unit: string): boolean {
  if (amount < 0) return false;
  if (unit === 'minutes' && amount > 1440) return false; // Max 24 hours
  if (unit === 'meters' && amount > 100000) return false; // Max 100km
  if (unit === 'kilograms' && amount > 1000) return false; // Max 1 ton
  if (unit === 'reps' && amount > 10000) return false; // Max 10k reps
  return true;
}

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
}

export function throttle<T extends (...args: any[]) => void>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  };
}

export function isValidDate(dateString: string): boolean {
  const date = new Date(dateString);
  return !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}
